import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { LpUser } from '@/lib/linguaprozApi';
import { login as apiLogin } from '@/lib/linguaprozApi';

const TOKEN_KEY = 'lp_token';
const USER_KEY = 'lp_user';

// ----------------------------------------------------------------
// State interface
// ----------------------------------------------------------------

interface LinguaprozAuthState {
  user: LpUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// ----------------------------------------------------------------
// Context value (state + actions)
// ----------------------------------------------------------------

interface LinguaprozAuthContextValue extends LinguaprozAuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const LinguaprozAuthContext = createContext<LinguaprozAuthContextValue | undefined>(undefined);

// ----------------------------------------------------------------
// Provider
// ----------------------------------------------------------------

export function LinguaprozAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LinguaprozAuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Login action
  const login = useCallback(async (email: string, password: string) => {
    const { token, user } = await apiLogin(email, password);
    localStorage.setItem(TOKEN_KEY, token);
    setState({
      user,
      token,
      isLoading: false,
      isAuthenticated: true,
    });
    (window as any).posthog?.identify(user.id, {
      email: user.email,
      name: user.full_name,
      translator_type: user.translator_type,
      platform: 'linguaproz',
    });
  }, []);

  // Logout action
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setState({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  // Initialise on mount — check for SSO token first, then stored token
  useEffect(() => {
    const init = async () => {
      setState(prev => ({ ...prev, isLoading: true }));

      try {
        // Step 1: Check for SSO token in URL (?token=...)
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('token');

        if (ssoToken) {
          try {
            const res = await fetch('https://api.linguaproz.com/api/translator/validate-cat-token', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: ssoToken }),
            });
            const data = await res.json();

            if (res.ok && data.valid && data.cat_user_id && data.lp_session_token) {
              // Valid SSO token — fetch user profile and authenticate
              // Valid SSO — store LP session token, use returned user directly
              localStorage.setItem(TOKEN_KEY, data.lp_session_token);
              const user = data.user;
              localStorage.setItem(USER_KEY, JSON.stringify(user));
              // Strip token from URL without triggering a reload
              window.history.replaceState({}, '', window.location.pathname);
              setState({
                user,
                token: data.lp_session_token,
                isLoading: false,
                isAuthenticated: true,
              });
              (window as any).posthog?.identify(user.id, {
                email: user.email,
                name: user.full_name,
                translator_type: user.translator_type,
                platform: 'linguaproz',
                sso: true,
              });
              return;
            } else {
              // Invalid or expired SSO token — redirect to login with reason
              window.location.href = 'https://translators.linguaproz.com?reason=expired';
              return;
            }
          } catch {
            // Network error during SSO validation — fall through to stored token
            window.location.href = 'https://translators.linguaproz.com?reason=expired';
            return;
          }
        }

        // Step 2: No SSO token — check for existing stored token
        const storedToken = localStorage.getItem(TOKEN_KEY);

        if (storedToken) {
          let user: LpUser | null = null;
          const storedUserRaw = localStorage.getItem(USER_KEY);
          if (storedUserRaw) {
            try {
              user = JSON.parse(storedUserRaw);
            } catch {
              user = null;
            }
          }

          if (!user) {
            // No valid cached profile (e.g. a session predating this fix) —
            // treat as invalid rather than authenticating with a null user
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setState({
              user: null,
              token: null,
              isLoading: false,
              isAuthenticated: false,
            });
          } else {
            setState({
              user,
              token: storedToken,
              isLoading: false,
              isAuthenticated: true,
            });
            (window as any).posthog?.identify(user.id, {
              email: user.email,
              name: user.full_name,
              translator_type: user.translator_type,
              platform: 'linguaproz',
            });
          }
        } else {
          setState({
            user: null,
            token: null,
            isLoading: false,
            isAuthenticated: false,
          });
        }
      } catch {
        // Token invalid or expired — clear storage, stay unauthenticated
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setState({
          user: null,
          token: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    };

    init();
  }, []);

  const value: LinguaprozAuthContextValue = {
    ...state,
    login,
    logout,
  };

  return (
    <LinguaprozAuthContext.Provider value={value}>
      {children}
    </LinguaprozAuthContext.Provider>
  );
}

// ----------------------------------------------------------------
// Hook
// ----------------------------------------------------------------

export function useLinguaprozAuth(): LinguaprozAuthContextValue {
  const context = useContext(LinguaprozAuthContext);
  if (context === undefined) {
    throw new Error('useLinguaprozAuth must be used within a LinguaprozAuthProvider');
  }
  return context;
}
